import { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { Bot, X, Send, Loader2, Sparkles, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAppContext } from "@/App";

const STORAGE_KEY = "pigeon-ai-history";

export default function AIAssistant() {
  const { session } = useAppContext();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const hubId = session?.hubId;
  const lang = localStorage.getItem("pigeon-lang") || "en";

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || !hubId || loading) return;

    const userMsg = { role: "user", content: text };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const res = await api.aiChat(hubId, text, messages, lang);
      setMessages([...next, { role: "assistant", content: res.reply }]);
    } catch (e) {
      const errMsg = e?.response?.data?.detail || "AI service unavailable";
      setMessages([...next, { role: "assistant", content: `Error: ${errMsg}` }]);
    }
    setLoading(false);
  };

  const clearHistory = () => {
    setMessages([]);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  if (!hubId) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="ai-assistant-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-red-600 hover:bg-red-500 text-white rounded-2xl shadow-xl flex items-center justify-center transition-all hover:scale-105 active:scale-95"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                <Bot className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-bold tracking-tight">Philogic</p>
                <p className="text-[10px] text-slate-400 font-medium">Hub AI Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="ai-clear-btn"
                onClick={clearHistory}
                className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
                title="Clear chat"
              >
                <Trash2 className="w-3.5 h-3.5 text-slate-400" />
              </button>
              <button
                data-testid="ai-close-btn"
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0" style={{ maxHeight: "400px" }}>
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-red-500" />
                </div>
                <p className="text-sm font-bold text-slate-700 mb-1">Philogic AI</p>
                <p className="text-xs text-slate-400 leading-relaxed max-w-[240px] mx-auto">
                  Ask about your hub — delayed requests, driver availability, route suggestions, or any operations question.
                </p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`ai-msg-${msg.role}-${i}`}
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-red-600 text-white rounded-br-md"
                      : "bg-slate-100 text-slate-800 rounded-bl-md"
                  }`}
                >
                  <MessageContent content={msg.content} />
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-md">
                  <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-slate-200 px-3 py-2.5 bg-slate-50">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                placeholder="Ask Philogic..."
                className="flex-1 bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-red-400 focus:ring-1 focus:ring-red-100 transition-all placeholder:text-slate-400"
                disabled={loading}
              />
              <button
                data-testid="ai-send-btn"
                onClick={send}
                disabled={!input.trim() || loading}
                className="w-10 h-10 bg-red-600 hover:bg-red-500 disabled:bg-slate-300 text-white rounded-xl flex items-center justify-center transition-all shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[9px] text-slate-400 mt-1.5 text-center font-medium">
              Powered by Llama 3.3 via Groq
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function MessageContent({ content }) {
  // Simple markdown-like rendering for bullet points and bold
  const lines = content.split("\n");
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (!line.trim()) return null;
        const isBullet = line.trim().startsWith("- ") || line.trim().startsWith("* ");
        const formatted = line
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/`(.+?)`/g, '<code class="bg-slate-200 px-1 rounded text-xs">$1</code>');
        if (isBullet) {
          return (
            <div key={i} className="flex items-start gap-1.5 ml-1">
              <span className="mt-1.5 w-1 h-1 bg-current rounded-full shrink-0 opacity-50" />
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatted.replace(/^[-*]\s*/, "")) }} />
            </div>
          );
        }
        return <p key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatted) }} />;
      })}
    </div>
  );
}
